import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import messages from 'modules/booking/messages';
import { ReactComponent as UsersAddIcon } from 'img/icons/users-add.svg';
import {
  EventOverbookingType as OverbookingType,
  IEventPersonEntity,
  PersonAppointmentStatus,
} from 'modules/booking/interfaces';
import { ActionItem } from 'common/components';
import { publicCase } from 'common/utils/stringFormat';
import { isLimitedEvent } from 'utils/booking.utils';
import ActionItemWarningIcon from 'common/components/ActionsMenu/ActionItemWarningIcon';
import BookingActionItemContainer from 'components/Booking/BookingActionItemContainer';

interface IBookingActionItemConfirmParticipation {
  onClick: () => void;
  userId: number;
  eventPersons: IEventPersonEntity[];
  appointmentStatus: PersonAppointmentStatus;
  overbookingType: OverbookingType;
  spotLimited: boolean;
  bookedSpots: number;
  spotAmount: number;
  isEventCanceled: boolean;
}

const BookingActionItemConfirmParticipation: FC<IBookingActionItemConfirmParticipation> = ({
  onClick,
  userId,
  eventPersons,
  appointmentStatus,
  overbookingType,
  spotLimited,
  bookedSpots,
  spotAmount,
  isEventCanceled,
}) => {
  const intl = useIntl();
  const person = eventPersons.find(item => item.customer?.id === userId);
  const isUserInEvent = !!person;
  const isUserInWaitList = person && person.waitList ? person.waitList : false;
  const isCancelled = appointmentStatus === PersonAppointmentStatus.Canceled;
  const isPending = appointmentStatus === PersonAppointmentStatus.Pending;
  const allowConfirm = isCancelled || isPending;
  let disableReason: JSX.Element | null = null;

  const isLimited = (): boolean => {
    if (isCancelled) {
      return isLimitedEvent(overbookingType, bookedSpots, spotAmount, spotLimited);
    }

    return false;
  };

  if (isEventCanceled) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.eventIsCanceled)} />
    );
  } else if (!isUserInEvent) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.userIsNotInEvent)} />
    );
  } else if (isUserInWaitList) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.userInWaitList)} />
    );
  } else if (!allowConfirm) {
    const tooltipMessage = intl.formatMessage(messages.confirmRestrictionByStatus, {
      status: publicCase(appointmentStatus),
    });

    disableReason = <ActionItemWarningIcon tooltipMessage={tooltipMessage} />;
  } else if (isLimited()) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.limitedSpots)} />
    );
  }

  const disabled = !!disableReason;

  return (
    <ActionItem
      key="confirmParticipationAction"
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      icon={<UsersAddIcon />}
    >
      <BookingActionItemContainer message={intl.formatMessage(messages.confirmParticipation)}>
        {disableReason}
      </BookingActionItemContainer>
    </ActionItem>
  );
};

export default BookingActionItemConfirmParticipation;
